import type { TravelLog } from './types'
import { isEmbedMode, state } from './state'
import { els } from './dom'
import { t } from './i18n'
import { saveItems, deleteMedia } from './storage'
import { renderMedia, revokeCachedMedia } from './media'
import { map } from './map'
import { renderEditPopup } from './modal'

/** Refresh every translatable label and toggle states in the chrome. */
export function applyTranslations(): void {
  const eyebrowEl = document.querySelector<HTMLElement>("[data-i18n='eyebrow']")
  if (eyebrowEl) eyebrowEl.textContent = t('eyebrow')
  els.exportButton.textContent = t('exportBackup')
  if (els.importInput.parentElement?.firstChild) {
    els.importInput.parentElement.firstChild.textContent = `${t('importBackup')} `
  }
  document
    .querySelector('.stats-grid')
    ?.setAttribute('aria-label', t('statsLabel'))
  const statSpans = document.querySelectorAll<HTMLElement>('.stat span')
  if (statSpans[0]) statSpans[0].textContent = t('countries')
  if (statSpans[1]) statSpans[1].textContent = t('logs')
  if (statSpans[2]) statSpans[2].textContent = t('media')
  els.clearButton.title = t('clearAll') ?? 'Clear all'
  els.themeLabel.textContent = t('themeLabel')
  els.languageLabel.textContent = t('languageLabel')
  const lightButton = els.themeButtons.find(
    (button) => button.dataset.themeChoice === 'light',
  )
  const darkButton = els.themeButtons.find(
    (button) => button.dataset.themeChoice === 'dark',
  )
  if (lightButton) lightButton.textContent = t('light')
  if (darkButton) darkButton.textContent = t('dark')
  els.themeButtons.forEach((button) =>
    button.classList.toggle(
      'is-active',
      button.dataset.themeChoice === state.theme,
    ),
  )
  els.languageButtons.forEach((button) =>
    button.classList.toggle(
      'is-active',
      button.dataset.langChoice === state.lang,
    ),
  )
  els.drawerToggle.textContent = els.drawer.classList.contains('is-collapsed')
    ? t('drawerClosed')
    : t('drawerOpen')
  els.drawerToggle.setAttribute(
    'aria-expanded',
    String(!els.drawer.classList.contains('is-collapsed')),
  )
  if (
    els.mapStatus.textContent === 'Loading map...' ||
    els.mapStatus.textContent === 'Carregando mapa...'
  ) {
    els.mapStatus.textContent = t('loadingMap')
  }
  if (!state.selectedCountry) {
    els.selectedCountryTitle.textContent = t('selectCountry')
    els.countrySummary.textContent = t('clickCountry')
  }
}

export function renderStats(): void {
  const countries = new Set(
    state.items.map((item) => item.country).filter(Boolean),
  )
  els.countryCount.textContent = String(countries.size)
  // els.cityCount.textContent = String(
  //   state.items.filter((item) => item.kind === 'travel-log').length,
  // )
  els.photoCount.textContent = String(
    state.items.reduce((sum, item) => sum + item.media.length, 0),
  )
}

/** Re-render the side-drawer list of items for the currently selected region. */
export function renderCountryDetails(onMutation: () => void): void {
  const country = state.selectedCountry

  if (!country) {
    els.selectedCountryTitle.textContent = t('selectCountry')
    els.countrySummary.textContent = t('clickCountry')
    els.visitList.replaceChildren()
    return
  }

  const items = state.items.filter((item) => item.country === country)
  els.selectedCountryTitle.textContent = country || t('selectCountry')
  els.countrySummary.textContent =
    items.length === 0
      ? t('noItems')
      : `${items.length} ${t('media').toLowerCase()} / ${t('logs').toLowerCase()}`
  els.visitList.replaceChildren(
    ...items.map((item) => renderItemCard(item, onMutation)),
  )
}

function renderItemCard(item: TravelLog, onMutation: () => void): HTMLElement {
  const node = els.visitCardTemplate.content.firstElementChild!.cloneNode(
    true,
  ) as HTMLElement
  const mediaFrame = node.querySelector<HTMLElement>('.visit-photo-frame')!
  const title = node.querySelector<HTMLElement>('h3')!
  const meta = node.querySelector<HTMLElement>('.visit-meta')!
  const note = node.querySelector<HTMLElement>('p')!
  const deleteButton = node.querySelector<HTMLButtonElement>('.delete-button')!

  title.textContent = item.title
  meta.textContent = [
    t('travelLog'),
    item.visitedAt || t('noDate'),
    `${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}`,
  ]
    .filter(Boolean)
    .join(' · ')
  note.textContent = item.note || t('noNote')
  void renderMedia(mediaFrame, item)
  deleteButton.textContent = t('delete')
  deleteButton.addEventListener('click', async () => {
    state.items = state.items.filter((current) => current.id !== item.id)
    const blobsToDelete = item.media
      .filter((m) => m.source === 'indexeddb' && m.blobId)
      .map((m) => m.blobId)
    await Promise.all(blobsToDelete.map(deleteMedia))
    revokeCachedMedia(blobsToDelete)
    saveItems(state.items)
    onMutation()
  })

  // Edit button — inserted before delete, hidden in embed mode (read-only).
  if (!isEmbedMode) {
    const editButton = document.createElement('button')
    editButton.type = 'button'
    editButton.className = 'edit-button'
    editButton.textContent = t('edit')
    editButton.addEventListener('click', () => {
      renderEditPopup(item, onMutation)
    })
    deleteButton.parentElement!.insertBefore(editButton, deleteButton)
  }

  return node
}

export function toggleDrawer(): void {
  els.drawer.classList.toggle('is-collapsed')
  applyTranslations()
  window.requestAnimationFrame(() => map.invalidateSize())
}
